import React from 'react';
import { Card, Typography, Collapse, Divider, List, Progress, Button, Radio, Checkbox, Input, Space, Tag, Steps } from 'antd';
import { TrophyOutlined, ClockCircleOutlined, CheckCircleOutlined, BookOutlined, SettingOutlined, PlayCircleOutlined, ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import './EmployeeInstructions.css';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const EmployeeInstructions: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  return (
    <section className="employee-instructions" aria-labelledby="employee-instructions-title">
      <Card
        className="employee-instructions__card"
        title={
          <Title level={3} id="employee-instructions-title">
            <BookOutlined style={{ marginRight: 8 }} />
            {t('dashboard.instructions.title')}
          </Title>
        }
      >
        <Collapse ghost>
          <Panel 
            header={<span style={{ fontSize: '16px', fontWeight: 500 }}>{t('dashboard.instructions.passing.title')}</span>} 
            key="1"
          >
            <Steps
              direction="vertical"
              size="small"
              items={[
                {
                  title: t('dashboard.instructions.passing.step1'),
                  status: 'finish',
                  icon: <PlayCircleOutlined />,
                },
                {
                  title: t('dashboard.instructions.passing.step2'),
                  status: 'finish',
                  icon: <CheckCircleOutlined />,
                },
                {
                  title: t('dashboard.instructions.passing.step3'),
                  status: 'process',
                  icon: <BookOutlined />,
                },
                {
                  title: t('dashboard.instructions.passing.step4'),
                  status: 'wait',
                  icon: <ArrowRightOutlined />,
                },
                {
                  title: t('dashboard.instructions.passing.step5'),
                  status: 'wait',
                  icon: <ClockCircleOutlined />,
                },
                {
                  title: t('dashboard.instructions.passing.step6'),
                  status: 'wait',
                  icon: <CheckCircleOutlined />,
                },
                {
                  title: t('dashboard.instructions.passing.step7'),
                  status: 'wait',
                  icon: <TrophyOutlined />,
                },
              ]}
              style={{ marginBottom: 24 }}
            />
            
            <Divider orientation="left">{t('dashboard.instructions.visual.title')}</Divider>
            
            {/* Визуализация: Список тестов */}
            <Card
              size="small"
              title={
                <Space>
                  <PlayCircleOutlined />
                  <span>{t('dashboard.instructions.visual.testList')}</span>
                </Space>
              }
              style={{ 
                marginBottom: 16,
                border: '2px dashed #d9d9d9',
                background: '#fafafa'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ 
                  padding: '12px',
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  background: '#fff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Text strong>Тест по ассортименту</Text>
                    <Tag color="blue">Ассортимент</Tag>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Tag>20 вопросов</Tag>
                    <Tag>30 минут</Tag>
                  </div>
                  <Button type="primary" icon={<PlayCircleOutlined />} size="small" block>
                    {t('tests.start')}
                  </Button>
                </div>
              </Space>
            </Card>

            {/* Визуализация: Интерфейс теста */}
            <Card
              size="small"
              title={
                <Space>
                  <BookOutlined />
                  <span>{t('dashboard.instructions.visual.testInterface')}</span>
                </Space>
              }
              style={{ 
                marginBottom: 16,
                border: '2px dashed #d9d9d9',
                background: '#fafafa'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {/* Прогресс и таймер */}
                <div style={{ 
                  padding: '12px',
                  background: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e8e8e8',
                  marginBottom: 12
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Text strong>Вопрос 5 из 20</Text>
                    <Text type="danger">
                      <ClockCircleOutlined /> 25:30
                    </Text>
                  </div>
                  <Progress percent={25} showInfo={false} />
                </div>

                {/* Вопрос */}
                <div style={{ 
                  padding: '16px',
                  background: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e8e8e8',
                  marginBottom: 12
                }}>
                  <Text strong style={{ fontSize: '16px' }}>
                    Какой артикул соответствует данной детали?
                  </Text>
                </div>

                {/* Варианты ответов */}
                <div style={{ 
                  padding: '12px',
                  background: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e8e8e8',
                  marginBottom: 12
                }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ 
                      padding: '8px 12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      background: '#f0f0f0'
                    }}>
                      <Radio checked={false}>Вариант ответа 1</Radio>
                    </div>
                    <div style={{ 
                      padding: '8px 12px',
                      border: '2px solid #1890ff',
                      borderRadius: '4px',
                      background: '#e6f7ff'
                    }}>
                      <Radio checked={true}>Вариант ответа 2 (выбран)</Radio>
                    </div>
                    <div style={{ 
                      padding: '8px 12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      background: '#f0f0f0'
                    }}>
                      <Radio checked={false}>Вариант ответа 3</Radio>
                    </div>
                    <div style={{ 
                      padding: '8px 12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      background: '#f0f0f0'
                    }}>
                      <Radio checked={false}>Вариант ответа 4</Radio>
                    </div>
                  </Space>
                </div>

                {/* Кнопки навигации */}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button icon={<ArrowLeftOutlined />} disabled>
                    {t('test.back')}
                  </Button>
                  <Button type="primary" icon={<ArrowRightOutlined />}>
                    {t('test.next')}
                  </Button>
                </div>
              </Space>
            </Card>

            {/* Визуализация: Результаты */}
            <Card
              size="small"
              title={
                <Space>
                  <TrophyOutlined />
                  <span>{t('dashboard.instructions.visual.results')}</span>
                </Space>
              }
              style={{ 
                border: '2px dashed #d9d9d9',
                background: '#fafafa'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ 
                  padding: '16px',
                  background: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e8e8e8',
                  textAlign: 'center'
                }}>
                  <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                    Тест пройден!
                  </Title>
                  <div style={{ marginTop: 16 }}>
                    <Text strong>Результат: </Text>
                    <Text style={{ fontSize: '24px', color: '#52c41a', fontWeight: 'bold' }}>
                      85%
                    </Text>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Space>
                      <Tag color="success">
                        <CheckOutlined /> Правильных: 17
                      </Tag>
                      <Tag color="error">
                        <CloseOutlined /> Неправильных: 3
                      </Tag>
                    </Space>
                  </div>
                </div>
              </Space>
            </Card>
          </Panel>
          
          <Panel 
            header={<span style={{ fontSize: '16px', fontWeight: 500 }}>{t('dashboard.instructions.types.title')}</span>} 
            key="2"
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Одиночный выбор */}
              <Card
                size="small"
                title={<Text strong>{t('dashboard.instructions.types.single.title')}</Text>}
                style={{ border: '1px solid #e8e8e8' }}
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {t('dashboard.instructions.types.single.desc')}
                </Text>
                <div style={{ 
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8'
                }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Какой артикул соответствует данной детали?
                  </Text>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio checked={false}>FEBEST-12345</Radio>
                    <Radio checked={true}>FEBEST-67890 (выбран)</Radio>
                    <Radio checked={false}>FEBEST-11111</Radio>
                  </Space>
                </div>
              </Card>

              {/* Множественный выбор */}
              <Card
                size="small"
                title={<Text strong>{t('dashboard.instructions.types.multiple.title')}</Text>}
                style={{ border: '1px solid #e8e8e8' }}
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {t('dashboard.instructions.types.multiple.desc')}
                </Text>
                <div style={{ 
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8'
                }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Выберите все правильные варианты:
                  </Text>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Checkbox checked={true}>Вариант 1 (выбран)</Checkbox>
                    <Checkbox checked={true}>Вариант 2 (выбран)</Checkbox>
                    <Checkbox checked={false}>Вариант 3</Checkbox>
                    <Checkbox checked={false}>Вариант 4</Checkbox>
                  </Space>
                </div>
              </Card>

              {/* Открытый вопрос */}
              <Card
                size="small"
                title={<Text strong>{t('dashboard.instructions.types.open.title')}</Text>}
                style={{ border: '1px solid #e8e8e8' }}
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {t('dashboard.instructions.types.open.desc')}
                </Text>
                <div style={{ 
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8'
                }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Введите название детали:
                  </Text>
                  <Input placeholder="Введите ответ..." />
                </div>
              </Card>

              {/* Соответствие */}
              <Card
                size="small"
                title={<Text strong>{t('dashboard.instructions.types.matching.title')}</Text>}
                style={{ border: '1px solid #e8e8e8' }}
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {t('dashboard.instructions.types.matching.desc')}
                </Text>
                <div style={{ 
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8'
                }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Сопоставьте детали с артикулами:
                  </Text>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      background: '#fff',
                      borderRadius: '4px',
                      border: '1px solid #d9d9d9'
                    }}>
                      <Text style={{ flex: 1 }}>Деталь A</Text>
                      <Text style={{ margin: '0 12px', color: '#999' }}>→</Text>
                      <Text style={{ flex: 1, color: '#1890ff' }}>FEBEST-001</Text>
                    </div>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      background: '#fff',
                      borderRadius: '4px',
                      border: '1px solid #d9d9d9'
                    }}>
                      <Text style={{ flex: 1 }}>Деталь B</Text>
                      <Text style={{ margin: '0 12px', color: '#999' }}>→</Text>
                      <Text style={{ flex: 1, color: '#1890ff' }}>FEBEST-002</Text>
                    </div>
                  </Space>
                </div>
              </Card>

              {/* Определение по изображению */}
              <Card
                size="small"
                title={<Text strong>{t('dashboard.instructions.types.image.title')}</Text>}
                style={{ border: '1px solid #e8e8e8' }}
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {t('dashboard.instructions.types.image.desc')}
                </Text>
                <div style={{ 
                  padding: '12px',
                  background: '#fafafa',
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8'
                }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Определите деталь по изображению:
                  </Text>
                  <div style={{ 
                    width: '100%',
                    height: '120px',
                    background: '#e8e8e8',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                    border: '1px solid #d9d9d9'
                  }}>
                    <Text type="secondary">[Изображение детали]</Text>
                  </div>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input placeholder="Название детали" />
                    <Input placeholder="Артикул" />
                  </Space>
                </div>
              </Card>
            </Space>
          </Panel>
          
          {user?.role === 'admin' && (
            <Panel 
              header={
                <span style={{ fontSize: '16px', fontWeight: 500 }}>
                  <SettingOutlined style={{ marginRight: 8 }} />
                  {t('dashboard.instructions.admin.title')}
                </span>
              } 
              key="3"
            >
              <List
                dataSource={[
                  t('dashboard.instructions.admin.step1'),
                  t('dashboard.instructions.admin.step2'),
                  t('dashboard.instructions.admin.step3'),
                  t('dashboard.instructions.admin.step4'),
                  t('dashboard.instructions.admin.step5'),
                  t('dashboard.instructions.admin.step6'),
                ]}
                renderItem={(item, index) => (
                  <List.Item style={{ border: 'none', padding: '8px 0' }}>
                    <Text>
                      <strong>{index + 1}.</strong> {item}
                    </Text>
                  </List.Item>
                )}
              />
            </Panel>
          )}
        </Collapse>
      </Card>
    </section>
  );
};

export default EmployeeInstructions;
